// @custom — BrandKit: global brand identity provider
//
// Fetches brand metadata from GET /api/brand and applies it at runtime:
//   • <html lang> — from brand locale or brand.json company language
//   • <title>     — site name (brand.json companyName)
//   • CSS custom properties for brand palette overrides (future dynamic use)
//
// Falls back silently when the API is unreachable (offline / dev without server).
//
// This component is mounted by ThemeContext.jsx so every page inherits the kit
// without any change to @system/ or client/src/App.jsx.

import { useEffect, useRef } from 'react'
// Import brand-kit.css so webpack includes the utility styles in the bundle.
import './brand-kit.css'

/** Prefix for the injected <style> element */
const STYLE_ID = 'brand-kit-runtime'

/**
 * Apply the brand JSON payload to the document.
 * @param {{ companyName?: string, tagline?: string, lang?: string, primaryColor?: string, accentColor?: string }} payload
 */
function applyBrandPayload(payload) {
  // ── <html lang> ──────────────────────────────────────────────────────
  const lang = payload.lang || 'en'
  const htmlEl = document.documentElement
  if (htmlEl && htmlEl.getAttribute('lang') !== lang) {
    htmlEl.setAttribute('lang', lang)
  }

  // ── <title> ───────────────────────────────────────────────────────────
  const name = payload.companyName || ''
  const tagline = payload.tagline || ''
  const titleText = tagline ? `${name} | ${tagline}` : name
  if (titleText) {
    const existing = document.querySelector('title')
    if (existing) {
      if (existing.textContent !== titleText) existing.textContent = titleText
    } else {
      const el = document.createElement('title')
      el.textContent = titleText
      document.head.appendChild(el)
    }
  }

  // ── Runtime CSS variables (extend the build-time palette) ─────────────
  const primary = payload.primaryColor || ''
  const accent = payload.accentColor || ''
  if (primary || accent) {
    let style = /** @type {HTMLStyleElement | null} */ (document.getElementById(STYLE_ID))
    if (!style) {
      style = document.createElement('style')
      style.id = STYLE_ID
      document.head.appendChild(style)
    }
    const parts = [':root {']
    if (primary) parts.push(`--brand-primary: ${primary};`)
    if (accent) parts.push(`--brand-accent: ${accent};`)
    parts.push('}')
    style.textContent = parts.join(' ')
  }
}

/**
 * @param {WithChildren} props
 */
export function BrandKit({ children }) {
  const applied = useRef(false)

  useEffect(() => {
    if (applied.current) return
    // No fetch in this environment (jsdom tests, older browsers, SSR) — fall
    // back silently and let the build-time brand defaults stand.
    if (typeof fetch !== 'function') return

    const controller = new AbortController()
    const { signal } = controller

    fetch('/api/brand', { signal, credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return // silent fallback — not every deployment has /api/brand
        const payload = /** @type {any} */ (await res.json())
        applyBrandPayload(payload)
      })
      .catch(() => {
        // network errors are fine offline / in test
      })

    applied.current = true

    return () => controller.abort()
  }, [])

  return children
}

export default { BrandKit }